import Layout from "@/components/Layout";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import {
  attendanceApiErrorMessage,
  deleteAttendanceRecordsByMonth,
  fetchAttendanceMonthEmployeeSummaries,
  fetchAttendanceRecords,
  fetchAttendanceRecordsByMonth,
  finalizeAttendanceRecords,
  processAttendanceRecords,
  type AttendanceMonthEmployeeSummaryRow,
  type AttendanceMonthGroupRow,
  type AttendanceRecordRow,
} from "@/lib/hr/attendanceStore";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import { listDesignations, syncDesignationsFromApi } from "@/lib/hr/designationsStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import {
  endOfDay,
  endOfMonth,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import { motion } from "framer-motion";
import {
  Briefcase,
  CalendarDays,
  CalendarIcon,
  Check,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Search,
  Trash2,
  Users,
  Wand2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const EMPLOYEE_TYPES = ["Permanent", "Contract", "Probation", "Temporary"] as const;
const EMPLOYEE_STATUSES = ["Active", "Inactive"] as const;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

type MonthAttendanceGroup = AttendanceMonthGroupRow & {
  monthStart: Date;
};

function isActiveEmployee(employee: EmployeeRecord): boolean {
  const status = (employee.employeeStatus ?? "").trim();
  return status === "" || status === "Active";
}

function employeeDisplayName(employee: EmployeeRecord): string {
  return (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || employee.id;
}

/** Format decimal hours as "H:MM" (e.g. 290.466… → 290:28). */
function formatHoursMinutes(decimalHours: number): string {
  if (!Number.isFinite(decimalHours) || decimalHours <= 0) return "0:00";
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function formatTimeFromApi(value: string | null): string {
  if (!value?.trim()) return "—";
  const normalized = value.trim().replace(" ", "T");
  const d = parseISO(normalized);
  if (!isValid(d)) {
    const parts = value.trim().split(" ");
    return parts.length > 1 ? parts[1] : value;
  }
  return format(d, "h:mm a").toLowerCase();
}

function weekdayName(dateIso: string): string {
  const d = parseISO(dateIso.slice(0, 10));
  if (!isValid(d)) return "—";
  return format(d, "EEE");
}

function statusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "present") return "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300";
  if (s === "late") return "bg-orange-500/15 text-orange-800 dark:text-orange-300";
  if (s === "off_worked") return "bg-warning/15 text-warning";
  if (s === "absent") return "bg-destructive/15 text-destructive";
  if (s === "half_day") return "bg-amber-500/15 text-amber-900 dark:text-amber-300";
  if (s === "paid_leave") {
    return "bg-teal-600 text-white shadow-sm ring-1 ring-teal-700/40 dark:bg-teal-500 dark:text-teal-950 dark:ring-teal-300/50";
  }
  if (s === "unpaid_leave") {
    return "bg-rose-600 text-white shadow-sm ring-1 ring-rose-700/40 dark:bg-rose-500 dark:text-rose-950 dark:ring-rose-300/50";
  }
  if (s === "leave") return "bg-blue-500/15 text-blue-800 dark:text-blue-300";
  if (s === "holiday") return "bg-violet-500/15 text-violet-900 dark:text-violet-300";
  if (s === "off") return "bg-slate-600/30 text-slate-950 dark:bg-slate-500/35 dark:text-slate-100";
  return "bg-muted text-muted-foreground";
}

/** Full-row tint: matches Daily Attendance listing. */
function rowStatusClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "present") return "hover:bg-muted/20";
  if (s === "late") return "bg-orange-500/20 hover:bg-orange-500/25 dark:bg-orange-500/25 dark:hover:bg-orange-500/35 border-l-4 border-l-orange-500";
  if (s === "absent") return "bg-destructive/10 hover:bg-destructive/15";
  if (s === "off") return "bg-slate-600/28 hover:bg-slate-600/35 dark:bg-slate-500/35 dark:hover:bg-slate-500/45";
  if (s === "holiday") return "bg-violet-500/15 hover:bg-violet-500/20 dark:bg-violet-500/25 dark:hover:bg-violet-500/35";
  if (s === "off_worked") return "bg-warning/10 hover:bg-warning/15";
  if (s === "paid_leave") {
    return "bg-teal-500/20 hover:bg-teal-500/25 dark:bg-teal-500/25 dark:hover:bg-teal-500/35 border-l-4 border-l-teal-600";
  }
  if (s === "unpaid_leave") {
    return "bg-rose-500/20 hover:bg-rose-500/25 dark:bg-rose-500/25 dark:hover:bg-rose-500/35 border-l-4 border-l-rose-600";
  }
  if (s === "leave") return "bg-blue-500/10 hover:bg-blue-500/15";
  if (s === "half_day") return "bg-amber-500/10 hover:bg-amber-500/15";
  return "hover:bg-muted/20";
}

function statusLabel(status: string): string {
  const s = status.trim().toLowerCase();
  if (s === "off_worked") return "Off Day";
  if (s === "half_day") return "Half day";
  if (s === "holiday") return "Holiday";
  if (s === "paid_leave") return "Paid leave";
  if (s === "unpaid_leave") return "Unpaid leave";
  if (s === "leave") return "Leave";
  if (s === "late") return "Late";
  return s.replace(/_/g, " ");
}

function displayStatus(status: string, isLate: boolean): string {
  if (isLate && status.trim().toLowerCase() === "present") {
    return "late";
  }
  return status;
}

function minutesAsHoursDisplay(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  return formatHoursMinutes(minutes / 60);
}

export default function PayrollAttendances() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView =
    can(user, "attendance_daily_can_view_any") || can(user, "salary_process_can_view_any");
  const canGenerate =
    can(user, "attendance_daily_can_process_from_punches") ||
    can(user, "attendance_daily_can_process_current_month");
  const canDelete =
    canGenerate || can(user, "salary_process_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [months, setMonths] = useState<AttendanceMonthGroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateEmployeeIds, setGenerateEmployeeIds] = useState<string[]>([]);
  const [generateEmployeePickerOpen, setGenerateEmployeePickerOpen] = useState(false);
  const [generateEmployeeSearch, setGenerateEmployeeSearch] = useState("");
  const [viewMonth, setViewMonth] = useState<MonthAttendanceGroup | null>(null);
  const [viewRows, setViewRows] = useState<AttendanceMonthEmployeeSummaryRow[]>([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [detailEmployee, setDetailEmployee] = useState<AttendanceMonthEmployeeSummaryRow | null>(null);
  const [detailRows, setDetailRows] = useState<AttendanceRecordRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MonthAttendanceGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(new Date()));
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filterDepartmentId, setFilterDepartmentId] = useState<string>("__all__");
  const [filterDesignationId, setFilterDesignationId] = useState<string>("__all__");
  const [filterEmployeeType, setFilterEmployeeType] = useState<string>("__all__");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<
    | "latest"
    | "oldest"
    | "name_asc"
    | "name_desc"
    | "hours_desc"
    | "hours_asc"
    | "employees_desc"
    | "days_desc"
  >("latest");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const departments = useMemo(() => listDepartments(), [version]);
  const designations = useMemo(() => listDesignations(), [version]);
  const activeEmployees = useMemo(() => listEmployees().filter(isActiveEmployee), [version]);
  const generateSelectedEmployeeIdSet = useMemo(() => new Set(generateEmployeeIds), [generateEmployeeIds]);
  const filteredGenerateEmployees = useMemo(() => {
    const q = generateEmployeeSearch.trim().toLowerCase();
    if (!q) return activeEmployees;
    return activeEmployees.filter((e) => {
      const name = employeeDisplayName(e).toLowerCase();
      const code = (e.employeeCode ?? "").toLowerCase();
      return name.includes(q) || code.includes(q) || e.id.toLowerCase().includes(q);
    });
  }, [activeEmployees, generateEmployeeSearch]);
  const generateSelectedEmployees = useMemo(
    () => activeEmployees.filter((e) => generateSelectedEmployeeIdSet.has(e.id)),
    [activeEmployees, generateSelectedEmployeeIdSet],
  );

  const toggleGenerateEmployee = useCallback((id: string) => {
    setGenerateEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const loadMonths = useCallback(async () => {
    const data = await fetchAttendanceRecordsByMonth();
    setMonths(data);
    refresh();
  }, [refresh]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!canView) {
        setIsForbidden(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      setIsForbidden(false);
      try {
        await Promise.all([
          syncEmployeesFromApi(),
          syncDepartmentsFromApi(),
          syncDesignationsFromApi(),
        ]);
        // One row per month: GROUP BY YEAR(date), MONTH(date)
        await loadMonths();
      } catch (e) {
        if (mounted && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          setMonths([]);
          return;
        }
        if (mounted) {
          toast.error(attendanceApiErrorMessage(e));
          setMonths([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [canView, loadMonths]);

  const monthGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = new Date();
    const end = endOfDay(now);
    const start =
      quickDate === "today"
        ? startOfDay(now)
        : quickDate === "week"
          ? startOfDay(subDays(now, 6))
          : quickDate === "month"
            ? startOfMonth(now)
            : null;

    return months
      .filter((m) => {
        const monthStart = parseISO(m.dateFrom);
        if (start && isValid(monthStart)) {
          const monthEnd = parseISO(m.dateTo);
          if (!isValid(monthEnd) || monthEnd < start || monthStart > end) return false;
        }
        if (!q) return true;
        return m.monthLabel.toLowerCase().includes(q) || m.monthKey.includes(q);
      })
      .map((m) => {
        const monthStart = parseISO(m.dateFrom);
        return {
          ...m,
          monthStart: isValid(monthStart) ? monthStart : new Date(0),
        } satisfies MonthAttendanceGroup;
      });
  }, [months, query, quickDate]);

  const sorted = useMemo(() => {
    const items = [...monthGroups];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.monthKey.localeCompare(b.monthKey);
        case "name_asc":
          return a.monthLabel.localeCompare(b.monthLabel);
        case "name_desc":
          return b.monthLabel.localeCompare(a.monthLabel);
        case "hours_asc":
          return a.totalHours - b.totalHours;
        case "hours_desc":
          return b.totalHours - a.totalHours;
        case "employees_desc":
          return b.employeeCount - a.employeeCount;
        case "days_desc":
          return b.recordCount - a.recordCount;
        case "latest":
        default:
          return b.monthKey.localeCompare(a.monthKey);
      }
    });
    return items;
  }, [monthGroups, sortBy]);

  const appliedFiltersCount =
    (filterDepartmentId !== "__all__" ? 1 : 0) +
    (filterDesignationId !== "__all__" ? 1 : 0) +
    (filterEmployeeType !== "__all__" ? 1 : 0) +
    (filterStatus !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterDepartmentId}|${filterDesignationId}|${filterEmployeeType}|${filterStatus}|${filterCreatedDate?.toISOString() ?? ""}|${sorted.length}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, { defaultPageSize: 20 });

  const minPickerYear = 1980;
  const maxPickerYear = new Date().getFullYear() + 1;

  const openGenerateConfirm = () => {
    const employees =
      generateEmployeeIds.length > 0
        ? activeEmployees.filter((e) => generateSelectedEmployeeIdSet.has(e.id))
        : activeEmployees;
    if (generateEmployeeIds.length > 0 && employees.length === 0) {
      toast.error("Select at least one valid employee.");
      return;
    }
    const missingPolicy = employees.filter((e) => !(e.workPolicyId ?? "").trim());
    if (missingPolicy.length > 0) {
      const preview = missingPolicy.slice(0, 5).map(employeeDisplayName).join(", ");
      const extra = missingPolicy.length > 5 ? ` (and ${missingPolicy.length - 5} more)` : "";
      toast.error(
        generateEmployeeIds.length > 0
          ? `Assign a work policy before generating. Missing: ${preview}${extra}.`
          : `Assign a work policy to all active employees before generating. Missing: ${preview}${extra}.`,
      );
      return;
    }
    setGenerateConfirmOpen(true);
  };

  const runGenerate = async () => {
    const dateFrom = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
    const dateTo = format(endOfMonth(selectedMonth), "yyyy-MM-dd");
    const employeeIds = generateEmployeeIds.length > 0 ? generateEmployeeIds : undefined;

    setGenerating(true);
    try {
      // 1) Stage into temp_attendance_records
      const { message: stageMessage, stats } = await processAttendanceRecords({
        dateFrom,
        dateTo,
        includeNonWorkingDays: true,
        employeeIds,
      });

      // 2) Move temp → permanent attendance_records
      if (stats.recordsUpserted > 0) {
        const finalized = await finalizeAttendanceRecords();
        toast.success(`${stageMessage} ${finalized.message}`);
        await loadMonths();
      } else if (stats.skippedNoShift > 0) {
        toast.warning(stageMessage);
      } else {
        toast.success(stageMessage);
      }

      setGenerateOpen(false);
      setGenerateEmployeeIds([]);
      setGenerateEmployeeSearch("");
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const companyName = (user?.company_branding?.name ?? "").trim() || "Company";

  const viewTotals = useMemo(() => {
    return viewRows.reduce(
      (acc, r) => ({
        workedHours: acc.workedHours + r.workedHours,
        overtimeHours: acc.overtimeHours + r.overtimeHours,
        offDayOvertimeHours: acc.offDayOvertimeHours + (r.offDayOvertimeHours || 0),
        totalHours: acc.totalHours + r.totalHours,
        lateCount: acc.lateCount + r.lateCount,
        presentCount: acc.presentCount + r.presentCount,
        halfDayCount: acc.halfDayCount + r.halfDayCount,
        absentCount: acc.absentCount + r.absentCount,
        offCount: acc.offCount + r.offCount,
        paidLeaveCount: acc.paidLeaveCount + r.paidLeaveCount,
        unpaidLeaveCount: acc.unpaidLeaveCount + r.unpaidLeaveCount,
        offWorkedCount: acc.offWorkedCount + r.offWorkedCount,
        holidayCount: acc.holidayCount + r.holidayCount,
        totalDays: acc.totalDays + r.totalDays,
      }),
      {
        workedHours: 0,
        overtimeHours: 0,
        offDayOvertimeHours: 0,
        totalHours: 0,
        lateCount: 0,
        presentCount: 0,
        halfDayCount: 0,
        absentCount: 0,
        offCount: 0,
        paidLeaveCount: 0,
        unpaidLeaveCount: 0,
        offWorkedCount: 0,
        holidayCount: 0,
        totalDays: 0,
      },
    );
  }, [viewRows]);

  const detailTotals = useMemo(() => {
    return detailRows.reduce(
      (acc, r) => ({
        workedHours: acc.workedHours + (Number(r.workedHours) || 0),
        overtimeMinutes: acc.overtimeMinutes + (Number(r.overtimeMinutes) || 0),
        offDayOvertimeMinutes: acc.offDayOvertimeMinutes + (Number(r.offDayOvertimeMinutes) || 0),
        shortMinutes: acc.shortMinutes + (Number(r.shortMinutes) || 0),
        lateMinutes: acc.lateMinutes + (Number(r.lateMinutes) || 0),
      }),
      {
        workedHours: 0,
        overtimeMinutes: 0,
        offDayOvertimeMinutes: 0,
        shortMinutes: 0,
        lateMinutes: 0,
      },
    );
  }, [detailRows]);

  const hasSplitOt = useMemo(() => viewRows.some((r) => r.enableSplitOt), [viewRows]);
  const detailHasSplitOt = useMemo(() => detailRows.some((r) => r.enableSplitOt), [detailRows]);

  const openViewMonth = async (month: MonthAttendanceGroup) => {
    setViewMonth(month);
    setViewRows([]);
    setDetailEmployee(null);
    setDetailRows([]);
    setViewLoading(true);
    try {
      const year = month.monthStart.getFullYear();
      const mo = month.monthStart.getMonth() + 1;
      const { rows } = await fetchAttendanceMonthEmployeeSummaries({ year, month: mo });
      setViewRows(rows);
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
      setViewMonth(null);
    } finally {
      setViewLoading(false);
    }
  };

  const openEmployeeDetail = async (employee: AttendanceMonthEmployeeSummaryRow) => {
    if (!viewMonth) return;
    setDetailEmployee(employee);
    setDetailRows([]);
    setDetailLoading(true);
    try {
      const all: AttendanceRecordRow[] = [];
      let page = 1;
      let lastPage = 1;
      do {
        const { rows, meta } = await fetchAttendanceRecords({
          dateFrom: viewMonth.dateFrom,
          dateTo: viewMonth.dateTo,
          employeeId: employee.employeeId,
          page,
          perPage: 100,
          finalizedOnly: true,
        });
        all.push(...rows);
        lastPage = meta.lastPage;
        page += 1;
      } while (page <= lastPage);
      all.sort((a, b) => a.date.localeCompare(b.date));
      setDetailRows(all);
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
      setDetailEmployee(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const runDeleteMonth = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const year = deleteTarget.monthStart.getFullYear();
      const mo = deleteTarget.monthStart.getMonth() + 1;
      const { message } = await deleteAttendanceRecordsByMonth({ year, month: mo });
      toast.success(message);
      setDeleteTarget(null);
      await loadMonths();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!canView || isForbidden) {
    return (
      <PermissionDenied
        title="Attendances access required"
        message="You don’t have permission to view payroll attendances. Ask an administrator to grant attendance_daily_can_view_any or salary_process_can_view_any."
      />
    );
  }

  return (
    <Layout title="Attendances">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Attendances</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Finalized attendance grouped by month for payroll
            </p>
          </div>
          {canGenerate ? (
            <Button
              type="button"
              variant="premium"
              className="gap-2 shrink-0"
              onClick={() => {
                const now = startOfMonth(new Date());
                setSelectedMonth(now);
                setPickerYear(now.getFullYear());
                setGenerateEmployeeIds([]);
                setGenerateEmployeeSearch("");
                setGenerateEmployeePickerOpen(false);
                setGenerateOpen(true);
              }}
            >
              <Wand2 className="w-4 h-4" />
              Generate
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by month (e.g. June, 2026)…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter attendances</SheetTitle>
                  <SheetDescription>
                    Filters match the Employees page layout. Month list comes from SQL GROUP BY year/month.
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Department</Label>
                      <Select value={filterDepartmentId} onValueChange={setFilterDepartmentId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All departments" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All departments</SelectItem>
                          {departments.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Designation</Label>
                      <Select value={filterDesignationId} onValueChange={setFilterDesignationId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All designations" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All designations</SelectItem>
                          {designations.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee type</Label>
                      <Select value={filterEmployeeType} onValueChange={setFilterEmployeeType}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All types</SelectItem>
                          {EMPLOYEE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All statuses</SelectItem>
                          {EMPLOYEE_STATUSES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar
                            mode="single"
                            selected={filterCreatedDate}
                            onSelect={setFilterCreatedDate}
                            initialFocus
                          />
                          <div className="flex gap-2 border-t p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="flex-1"
                              onClick={() => setFilterCreatedDate(undefined)}
                            >
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                <SheetFooter className="mt-6 gap-2">
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full"
                    onClick={() => {
                      setFilterDepartmentId("__all__");
                      setFilterDesignationId("__all__");
                      setFilterEmployeeType("__all__");
                      setFilterStatus("__all__");
                      setFilterCreatedDate(undefined);
                    }}
                  >
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="payroll-attendances-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest month first</SelectItem>
                  <SelectItem value="oldest">Oldest month first</SelectItem>
                  <SelectItem value="name_asc">Month: A to Z</SelectItem>
                  <SelectItem value="name_desc">Month: Z to A</SelectItem>
                  <SelectItem value="hours_desc">Hours: High to Low</SelectItem>
                  <SelectItem value="hours_asc">Hours: Low to High</SelectItem>
                  <SelectItem value="employees_desc">Most employees</SelectItem>
                  <SelectItem value="days_desc">Most days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="payroll-attendances-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />

            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest month first</SelectItem>
                    <SelectItem value="oldest">Oldest month first</SelectItem>
                    <SelectItem value="name_asc">Month: A to Z</SelectItem>
                    <SelectItem value="name_desc">Month: Z to A</SelectItem>
                    <SelectItem value="hours_desc">Hours: High to Low</SelectItem>
                    <SelectItem value="hours_asc">Hours: Low to High</SelectItem>
                    <SelectItem value="employees_desc">Most employees</SelectItem>
                    <SelectItem value="days_desc">Most days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading attendances…
          </div>
        ) : null}

        {!loading && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {months.length === 0
              ? "No finalized attendance yet. Process and finalize from Daily attendance first."
              : "No attendances match your search."}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((g, i) => {
              const rangeLabel = isValid(g.monthStart)
                ? `${format(g.monthStart, "dd MMM")} – ${format(endOfMonth(g.monthStart), "dd MMM yyyy")}`
                : g.monthKey;
              return (
                <motion.div
                  key={g.monthKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="h-full"
                >
                  <CatalogEntityCard
                    className="h-full flex flex-col"
                    title={g.monthLabel}
                    titleIcon={CalendarDays}
                    statusBadge={{
                      label: (g.status || "processed").toLowerCase() === "processed" ? "Processed" : "Draft",
                      className:
                        (g.status || "processed").toLowerCase() === "processed"
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "bg-amber-500/10 text-amber-700 dark:text-amber-300",
                    }}
                    primaryMeta={{
                      label: "Employees",
                      value: String(g.employeeCount),
                      icon: Users,
                      iconClassName: "text-emerald-600",
                    }}
                    secondaryMeta={{
                      label: "Range",
                      value: rangeLabel,
                      icon: Briefcase,
                      iconClassName: "text-red-600",
                    }}
                    footer={`Present ${g.presentCount} · Absent ${g.absentCount} · Leave ${g.leaveCount} · Off ${g.offCount}`}
                    footerRight={`${g.totalHours.toFixed(1)}h`}
                    footerRightClassName="text-primary font-semibold"
                    onView={() => void openViewMonth(g)}
                    onDelete={canDelete ? () => setDeleteTarget(g) : undefined}
                  />
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((g, i) => {
              const rangeLabel = isValid(g.monthStart)
                ? `${format(g.monthStart, "dd MMM")} – ${format(endOfMonth(g.monthStart), "dd MMM yyyy")}`
                : g.monthKey;
              return (
                <motion.div
                  key={g.monthKey}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="glass-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                      <CalendarDays className="w-8 h-8 text-muted-foreground/40" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">{g.monthLabel}</h3>
                        <span
                          className={cn(
                            "text-[11px] px-2 py-0.5 rounded-full font-medium",
                            (g.status || "processed").toLowerCase() === "processed"
                              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                              : "bg-amber-500/10 text-amber-700 dark:text-amber-300",
                          )}
                        >
                          {(g.status || "processed").toLowerCase() === "processed" ? "Processed" : "Draft"}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-primary/10 text-primary">
                          {g.recordCount} day{g.recordCount === 1 ? "" : "s"}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {g.employeeCount} employee{g.employeeCount === 1 ? "" : "s"} · {rangeLabel}
                      </p>
                    </div>
                    <div className="text-right ml-auto">
                      <p className="text-sm font-semibold text-primary tabular-nums">{g.totalHours.toFixed(1)}h</p>
                      <p className="text-[11px] text-muted-foreground">
                        P {g.presentCount} · A {g.absentCount} · L {g.leaveCount} · O {g.offCount}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                      <Button
                        type="button"
                        variant="glass"
                        size="sm"
                        className="gap-1.5 h-8 text-xs"
                        onClick={() => void openViewMonth(g)}
                      >
                        <Eye className="w-3 h-3" /> View
                      </Button>
                      {canDelete ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                          onClick={() => setDeleteTarget(g)}
                          aria-label={`Delete ${g.monthLabel}`}
                          disabled={deleting}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : null}

        <GridPaginationFooter
          totalCount={totalCount}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <Dialog
        open={generateOpen}
        onOpenChange={(open) => {
          setGenerateOpen(open);
          if (!open) {
            setGenerateEmployeePickerOpen(false);
            setGenerateEmployeeIds([]);
            setGenerateEmployeeSearch("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col gap-4 overflow-hidden">
          <DialogHeader className="shrink-0 space-y-0">
            <DialogTitle>Generate Attendance</DialogTitle>
            <DialogDescription className="sr-only">Generate attendance for a month</DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-0.5">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Employees</Label>
              <Popover
                modal
                open={generateEmployeePickerOpen}
                onOpenChange={(next) => {
                  setGenerateEmployeePickerOpen(next);
                  if (next) setGenerateEmployeeSearch("");
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="glass-input h-10 w-full justify-between font-normal"
                    disabled={generating}
                  >
                    <span className={cn("truncate", generateEmployeeIds.length === 0 && "text-muted-foreground")}>
                      {generateEmployeeIds.length > 0
                        ? `${generateEmployeeIds.length} employee${generateEmployeeIds.length > 1 ? "s" : ""} selected`
                        : "Search and select employees…"}
                    </span>
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                  side="bottom"
                  sideOffset={4}
                  avoidCollisions={false}
                >
                  <div className="border-b border-border/50 p-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        autoFocus
                        className="h-9 pl-8"
                        placeholder="Search employees by name or code…"
                        value={generateEmployeeSearch}
                        onChange={(e) => setGenerateEmployeeSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="max-h-[220px] overflow-y-auto p-1">
                    {filteredGenerateEmployees.length === 0 ? (
                      <p className="px-2 py-6 text-center text-sm text-muted-foreground">No employees found.</p>
                    ) : (
                      filteredGenerateEmployees.map((e) => {
                        const selected = generateSelectedEmployeeIdSet.has(e.id);
                        const label = employeeDisplayName(e);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => toggleGenerateEmployee(e.id)}
                            className={cn(
                              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60",
                              selected && "bg-muted/40",
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                selected ? "border-primary/50 bg-primary/10" : "border-border",
                              )}
                            >
                              {selected ? <Check className="h-3 w-3 text-primary" /> : null}
                            </span>
                            <span className="flex-1 truncate">
                              {label}
                              {e.employeeCode ? (
                                <span className="text-muted-foreground"> ({e.employeeCode})</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
              <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={pickerYear <= minPickerYear}
                  onClick={() => setPickerYear((y) => Math.max(minPickerYear, y - 1))}
                  aria-label="Previous year"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-semibold tabular-nums text-foreground">{pickerYear}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={pickerYear >= maxPickerYear}
                  onClick={() => setPickerYear((y) => Math.min(maxPickerYear, y + 1))}
                  aria-label="Next year"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MONTH_LABELS.map((label, monthIndex) => {
                  const monthStart = new Date(pickerYear, monthIndex, 1);
                  const isSelected =
                    selectedMonth.getFullYear() === pickerYear && selectedMonth.getMonth() === monthIndex;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setSelectedMonth(startOfMonth(monthStart))}
                      className={cn(
                        "h-10 rounded-md text-sm font-medium transition-colors",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-background hover:bg-muted text-foreground border border-border/50",
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Selected:{" "}
                <span className="font-medium text-foreground">{format(selectedMonth, "MMMM yyyy")}</span>
              </p>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2 sm:gap-2">
            <Button type="button" variant="glass" onClick={() => setGenerateOpen(false)} disabled={generating}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="premium"
              className="gap-2"
              onClick={openGenerateConfirm}
              disabled={generating}
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={generateConfirmOpen}
        onOpenChange={setGenerateConfirmOpen}
        title="Generate Attendance?"
        description={
          generateSelectedEmployees.length === 0
            ? `Are you sure you want to generate attendance for all employees in ${format(selectedMonth, "MMMM yyyy")}?`
            : generateSelectedEmployees.length === 1
              ? `Are you sure you want to generate attendance for ${employeeDisplayName(generateSelectedEmployees[0]!)} in ${format(selectedMonth, "MMMM yyyy")}?`
              : `Are you sure you want to generate attendance for ${generateSelectedEmployees.length} selected employees in ${format(selectedMonth, "MMMM yyyy")}?`
        }
        mode="confirm"
        variant="warning"
        confirmLabel="Generate"
        onConfirm={() => {
          setGenerateConfirmOpen(false);
          void runGenerate();
        }}
      />

      <Dialog
        open={viewMonth !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewMonth(null);
            setViewRows([]);
            setDetailEmployee(null);
            setDetailRows([]);
          }
        }}
      >
        <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-6xl !flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border/50 bg-muted/20 px-6 py-4 pr-12">
            <DialogTitle className="text-xl font-display">
              Attendance {viewMonth?.monthLabel ?? ""}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Monthly employee attendance summary for {viewMonth?.monthLabel ?? "selected month"}
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
            {viewLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading summary…
              </div>
            ) : viewRows.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No employee attendance for this month.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-nowrap items-center gap-x-3 overflow-x-auto whitespace-nowrap text-[11px] text-muted-foreground">
                  <span><span className="font-semibold text-foreground">Hrs</span> Hours</span>
                  <span><span className="font-semibold text-foreground">OT</span> Overtime</span>
                  <span><span className="font-semibold text-foreground">OD OT</span> Off Day OT</span>
                  <span><span className="font-semibold text-foreground">Tot</span> Total hours</span>
                  <span><span className="font-semibold text-foreground">Late</span> Late days</span>
                  <span><span className="font-semibold text-foreground">P</span> Present</span>
                  <span><span className="font-semibold text-foreground">HD</span> Half day</span>
                  <span><span className="font-semibold text-foreground">A</span> Absent</span>
                  <span><span className="font-semibold text-foreground">Off</span> Off</span>
                  <span><span className="font-semibold text-foreground">PL</span> Paid leave</span>
                  <span><span className="font-semibold text-foreground">UL</span> Unpaid leave</span>
                  <span><span className="font-semibold text-foreground">OD</span> Off day</span>
                  <span><span className="font-semibold text-foreground">Hol</span> Holiday</span>
                  <span><span className="font-semibold text-foreground">Days</span> Total days</span>
                </div>
                <div className="overflow-x-auto rounded-xl border border-border/60 shadow-sm">
                  <table className="w-full min-w-[50rem] text-sm">
                  <thead>
                    <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="text-left font-semibold py-2 px-2.5 whitespace-nowrap">Employee</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Hours">Hrs</th>
                      {hasSplitOt ? (
                        <>
                          <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Shift Start Overtime">SOT</th>
                          <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Shift End Overtime">EOT</th>
                        </>
                      ) : (
                        <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Overtime">OT</th>
                      )}
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Off Day Overtime">OD OT</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Total hours">Tot</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Late days">Late</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Present">P</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Half day">HD</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Absent">A</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Off">Off</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Paid leave">PL</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Unpaid leave">UL</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Off day">OD</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Holiday">Hol</th>
                      <th className="text-right font-semibold py-2 px-2 whitespace-nowrap" title="Total days">Days</th>
                      <th className="text-center font-semibold py-2 px-2 whitespace-nowrap">Act</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewRows.map((r, idx) => (
                      <tr
                        key={r.employeeId}
                        className={cn(
                          "border-b border-border/40 last:border-0 cursor-pointer transition-colors",
                          idx % 2 === 0 ? "bg-background" : "bg-muted/15",
                          "hover:bg-primary/5",
                        )}
                        onDoubleClick={() => void openEmployeeDetail(r)}
                        title="Double-click to view attendance details"
                      >
                        <td className="py-2.5 px-3.5 font-medium text-foreground">{r.employeeName}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                          {formatHoursMinutes(r.workedHours)}
                        </td>
                        {hasSplitOt ? (
                          <>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                              {r.shiftStartOvertimeMinutes && r.shiftStartOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftStartOvertimeMinutes) : "—"}
                            </td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                              {r.shiftEndOvertimeMinutes && r.shiftEndOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftEndOvertimeMinutes) : "—"}
                            </td>
                          </>
                        ) : (
                          <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                            {formatHoursMinutes(r.overtimeHours)}
                          </td>
                        )}
                        <td className="py-2.5 px-3 text-right tabular-nums text-amber-600 font-medium dark:text-amber-400">
                          {r.offDayOvertimeHours && r.offDayOvertimeHours > 0 ? formatHoursMinutes(r.offDayOvertimeHours) : "—"}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-primary">
                          {formatHoursMinutes(r.totalHours)}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-orange-700 dark:text-orange-400">
                          {r.lateCount}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{r.presentCount}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-amber-700 dark:text-amber-400">
                          {r.halfDayCount}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{r.absentCount}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{r.offCount}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-teal-700 dark:text-teal-400">
                          {r.paidLeaveCount}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums text-rose-700 dark:text-rose-400">
                          {r.unpaidLeaveCount}
                        </td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{r.offWorkedCount}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums">{r.holidayCount}</td>
                        <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{r.totalDays}</td>
                        <td className="py-2 px-3 text-center">
                          <Button
                            type="button"
                            variant="glass"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              void openEmployeeDetail(r);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Detail
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border/60 bg-muted/50 text-sm font-semibold">
                      <td className="py-2.5 px-3.5">Total</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {formatHoursMinutes(viewTotals.workedHours)}
                      </td>
                      {hasSplitOt ? (
                        <>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {minutesAsHoursDisplay(viewRows.reduce((sum, r) => sum + (r.shiftStartOvertimeMinutes || 0), 0))}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {minutesAsHoursDisplay(viewRows.reduce((sum, r) => sum + (r.shiftEndOvertimeMinutes || 0), 0))}
                          </td>
                        </>
                      ) : (
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          {formatHoursMinutes(viewTotals.overtimeHours)}
                        </td>
                      )}
                      <td className="py-2.5 px-3 text-right tabular-nums text-amber-600 font-medium dark:text-amber-400">
                        {viewTotals.offDayOvertimeHours > 0 ? formatHoursMinutes(viewTotals.offDayOvertimeHours) : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-primary">
                        {formatHoursMinutes(viewTotals.totalHours)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-orange-700 dark:text-orange-400">
                        {viewTotals.lateCount}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.presentCount}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-amber-700 dark:text-amber-400">
                        {viewTotals.halfDayCount}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.absentCount}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.offCount}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-teal-700 dark:text-teal-400">
                        {viewTotals.paidLeaveCount}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-rose-700 dark:text-rose-400">
                        {viewTotals.unpaidLeaveCount}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.offWorkedCount}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.holidayCount}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{viewTotals.totalDays}</td>
                      <td className="py-2.5 px-3" />
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailEmployee !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailEmployee(null);
            setDetailRows([]);
          }
        }}
      >
        <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-5xl !flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border/50 bg-muted/20 px-6 py-4 pr-12">
            <DialogTitle className="text-lg font-display">
              {detailEmployee?.employeeName ?? "Employee"} · {viewMonth?.monthLabel ?? ""}
            </DialogTitle>
            <DialogDescription className="sr-only">Daily attendance details for selected employee</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
            {detailLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading details…
              </div>
            ) : detailRows.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No attendance records for this employee.</div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
                <table className="w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="text-left font-semibold py-2.5 px-3">Date</th>
                      <th className="text-left font-semibold py-2.5 px-3">Day</th>
                      <th className="text-left font-semibold py-2.5 px-3">Shift</th>
                      <th className="text-left font-semibold py-2.5 px-3">In</th>
                      <th className="text-left font-semibold py-2.5 px-3">Out</th>
                      <th className="text-right font-semibold py-2.5 px-3">Hours</th>
                      {detailHasSplitOt ? (
                        <>
                          <th className="text-right font-semibold py-2.5 px-3" title="Shift Start Overtime">SOT</th>
                          <th className="text-right font-semibold py-2.5 px-3" title="Shift End Overtime">EOT</th>
                        </>
                      ) : (
                        <th className="text-right font-semibold py-2.5 px-3">OT</th>
                      )}
                      <th className="text-right font-semibold py-2.5 px-3">ST</th>
                      <th className="text-right font-semibold py-2.5 px-3">Late</th>
                      <th className="text-center font-semibold py-2.5 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((r) => {
                      const shownStatus = displayStatus(r.status, r.isLate);
                      return (
                      <tr
                        key={r.id}
                        className={cn("border-b border-border/40 last:border-0", rowStatusClass(shownStatus))}
                      >
                        <td className="py-2 px-3 tabular-nums">{r.date}</td>
                        <td className="py-2 px-3 text-muted-foreground">{weekdayName(r.date)}</td>
                        <td className="py-2 px-3 text-muted-foreground">{r.workShiftName ?? "—"}</td>
                        <td className="py-2 px-3 tabular-nums text-muted-foreground">{formatTimeFromApi(r.checkIn)}</td>
                        <td className="py-2 px-3 tabular-nums text-muted-foreground">{formatTimeFromApi(r.checkOut)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {formatHoursMinutes(Number(r.workedHours) || 0)}
                        </td>
                        {detailHasSplitOt ? (
                          <>
                            <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                              {r.shiftStartOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftStartOvertimeMinutes) : "—"}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                              {r.shiftEndOvertimeMinutes > 0 ? minutesAsHoursDisplay(r.shiftEndOvertimeMinutes) : "—"}
                            </td>
                          </>
                        ) : (
                          <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                            {r.offDayOvertimeMinutes > 0 ? (
                              <span title="Off Day Overtime" className="text-amber-600 font-medium dark:text-amber-400">
                                {minutesAsHoursDisplay(r.offDayOvertimeMinutes)}
                              </span>
                            ) : (
                              minutesAsHoursDisplay(r.overtimeMinutes)
                            )}
                          </td>
                        )}
                        <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                          {r.shortMinutes > 0 ? minutesAsHoursDisplay(r.shortMinutes) : "—"}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                          {r.isLate || r.lateMinutes > 0 ? minutesAsHoursDisplay(r.lateMinutes) : "—"}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span
                            className={cn(
                              "inline-flex items-center text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full",
                              statusClass(shownStatus),
                            )}
                          >
                            {statusLabel(shownStatus)}
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border/60 bg-muted/50 text-sm font-semibold">
                      <td className="py-2.5 px-3" colSpan={5}>
                        Total
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {formatHoursMinutes(detailTotals.workedHours)}
                      </td>
                      {detailHasSplitOt ? (
                        <>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {minutesAsHoursDisplay(detailRows.reduce((sum, r) => sum + (r.shiftStartOvertimeMinutes || 0), 0))}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {minutesAsHoursDisplay(detailRows.reduce((sum, r) => sum + (r.shiftEndOvertimeMinutes || 0), 0))}
                          </td>
                        </>
                      ) : (
                        <td className="py-2.5 px-3 text-right tabular-nums">
                          <div className="flex flex-col items-end leading-tight text-xs">
                            <span>{minutesAsHoursDisplay(detailTotals.overtimeMinutes)}</span>
                            {detailTotals.offDayOvertimeMinutes > 0 && (
                              <span title="Off Day Overtime Total" className="text-amber-600 font-medium dark:text-amber-400">
                                {minutesAsHoursDisplay(detailTotals.offDayOvertimeMinutes)} <span className="text-[10px] font-normal text-muted-foreground">OD</span>
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {minutesAsHoursDisplay(detailTotals.shortMinutes)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {minutesAsHoursDisplay(detailTotals.lateMinutes)}
                      </td>
                      <td className="py-2.5 px-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
        title="Delete attendance month?"
        description={
          deleteTarget
            ? `Delete all finalized attendance for ${deleteTarget.monthLabel}? This cannot be undone.`
            : "Delete this month?"
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          void runDeleteMonth();
        }}
      />
    </Layout>
  );
}
